import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Download, Tag, ShoppingBag, CheckCircle, Trash2 } from 'lucide-react';

export function AdminMerchantsTab({
  merchantAnalytics, searchTerm, setSearchTerm,
  expandedUser, setExpandedUser, setSelectedUser, setShowDeleteDialog, exportMerchants
}) {
  const filteredMerchants = merchantAnalytics.filter(m =>
    !searchTerm || m.name?.toLowerCase().includes(searchTerm.toLowerCase()) || m.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar lojista..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-10" data-testid="merchant-search" />
        </div>
        <span className="text-sm text-slate-500">{filteredMerchants.length} lojistas</span>
        <Button size="sm" variant="outline" className="border-slate-300 text-xs ml-auto" onClick={exportMerchants} data-testid="export-merchants-btn">
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
      </div>

      <div className="space-y-2">
        {filteredMerchants.map(m => (
          <Card key={m.user_id} className="bg-white border border-slate-200" data-testid={`admin-merchant-${m.user_id}`}>
            <CardContent className="py-3">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedUser(expandedUser === m.user_id ? null : m.user_id)}>
                  {m.picture ? (
                    <img src={m.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-500 font-bold">{(m.company_name || m.name || 'L')[0]}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">{m.company_name || m.name}</p>
                    <p className="text-xs text-slate-500">{m.email} {m.city && `| ${m.city}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-xs capitalize ${
                    m.plan === 'black' ? 'bg-slate-900 text-white border-slate-800' :
                    m.plan === 'platinum' ? 'bg-slate-300 text-slate-800 border-slate-400' :
                    m.plan === 'premium' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                    'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>{m.plan || 'gratuito'}</Badge>
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    <Tag className="h-3 w-3 mr-1" />{m.total_coupons} cupons ({m.active_coupons} ativos)
                  </Badge>
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    <ShoppingBag className="h-3 w-3 mr-1" />{m.total_claims} resgates
                  </Badge>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />{m.total_validations} validados
                  </Badge>
                  <Button size="sm" variant="ghost" className="text-xs text-red-500 hover:text-red-700 h-7"
                    onClick={() => { setSelectedUser(m); setShowDeleteDialog(true); }}
                    data-testid={`delete-merchant-${m.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              {expandedUser === m.user_id && (
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-slate-400 text-xs">CNPJ</p><p className="text-slate-700">{m.cnpj || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Contato</p><p className="text-slate-700">{m.contact_name || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">WhatsApp</p><p className="text-slate-700">{m.whatsapp || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Email</p><p className="text-slate-700">{m.email || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Cidade</p><p className="text-slate-700">{m.city || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Bairro</p><p className="text-slate-700">{m.neighborhood || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Estado</p><p className="text-slate-700">{m.state || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Endereco Comercial</p><p className="text-slate-700">{m.commercial_address || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Cadastro</p><p className="text-slate-700">{m.created_at ? new Date(m.created_at).toLocaleDateString('pt-BR') : '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Conversao</p><p className="text-slate-700 font-semibold">{m.total_claims > 0 ? Math.round(m.total_validations / m.total_claims * 100) : 0}%</p></div>
                  <div className="col-span-2 md:col-span-4">
                    <p className="text-slate-400 text-xs mb-1">Categoria</p>
                    <div className="flex gap-1 flex-wrap">
                      {m.merchant_category ? (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{m.merchant_category}</Badge>
                      ) : <span className="text-xs text-slate-400">Nenhuma</span>}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
